import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useTranslation } from '../contexts/TranslationContext.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { useClickOutside } from '../hooks/useClickOutside.ts';
import { API_BASE } from '../utils/apiEndpoints';
import {
  getJobTitleSuggestions,
  isAllowedJobTitle,
  normalizeJobTitle,
} from '../config/jobTitles.ts';
import { localizeAchievementName } from '../utils/achievements.ts';
import './css/HrPage.css';

type ExportFormat = 'json' | 'csv' | 'xlsx';
const EXPORT_FORMATS: ExportFormat[] = ['json', 'csv', 'xlsx'];

interface HrAchievement {
  id: number;
  name: string;
  icon: string | null;
  points: number;
  key?: string | null;
}

interface HrUserItem {
  id: number;
  username: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  skills: string[] | null;
  avatar: string | null;
  job_title: string[] | null;
  university: string | null;
  role: string;
  email_verified: boolean | null;
  integrity_trust_level: string;
  createdAt: string;
  solved: number;
  acceptance: number;
  streak: number;
  maxStreak: number;
  activityDays30: number;
  points: number;
  rank: number | null;
  rankTotal: number | null;
  lastActivityDate: string | null;
  achievements: HrAchievement[];
}

interface HrFilters {
  jobTitles: string[];
  topPctFrom: number;
  topPctTo: number;
}

type SortBy = 'createdAt' | 'solved' | 'acceptance' | 'streak' | 'points' | 'username' | 'rank';

const DEFAULT_FILTERS: HrFilters = {
  jobTitles: [],
  topPctFrom: 0,
  topPctTo: 100,
};

function clampPercent(value: unknown, fallback: number): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(100, Math.max(0, Math.round(n)));
}

function normalizeTopPctRange(from: unknown, to: unknown): { topPctFrom: number; topPctTo: number } {
  let topPctFrom = clampPercent(from, 0);
  let topPctTo = clampPercent(to, 100);
  if (topPctFrom > topPctTo) {
    const tmp = topPctFrom;
    topPctFrom = topPctTo;
    topPctTo = tmp;
  }
  return { topPctFrom, topPctTo };
}

function isDefaultTopPct(f: HrFilters): boolean {
  return f.topPctFrom === 0 && f.topPctTo === 100;
}

function sanitizeFilterJobTitles(list: unknown): string[] {
  if (!Array.isArray(list)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of list) {
    if (typeof item !== 'string') continue;
    const normalized = normalizeJobTitle(item);
    if (!normalized) continue;
    const key = normalized.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(normalized);
  }
  return out;
}

function isHrOrAdmin(role?: string): boolean {
  return role === 'admin' || role === 'hr';
}

function shortlistKey(userId: string | number | undefined): string {
  return `sciplex_hr_shortlist_${userId ?? 'anon'}`;
}

function savedSearchKey(userId: string | number | undefined): string {
  return `sciplex_hr_saved_search_${userId ?? 'anon'}`;
}

function readShortlist(userId: string | number | undefined): number[] {
  try {
    const raw = localStorage.getItem(shortlistKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map(Number).filter((n) => Number.isFinite(n) && n > 0);
  } catch {
    return [];
  }
}

function writeShortlist(userId: string | number | undefined, ids: number[]) {
  try {
    localStorage.setItem(shortlistKey(userId), JSON.stringify(ids.slice(0, 500)));
  } catch {
    /* ignore quota */
  }
}

function formatName(u: HrUserItem): string {
  const parts = [u.firstName, u.lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : u.username;
}

function isEmailVerified(value: boolean | null): boolean {
  return value !== false;
}

function trustLabel(level: string, t: (k: string) => string): string {
  if (level === 'suspicious') return t('hr.integritySuspicious');
  if (level === 'cheater') return t('hr.integrityCheater');
  return t('hr.integrityTrusted');
}

function formatStatNumber(n: number): string {
  return Math.round(n).toLocaleString('ru-RU');
}

function formatRelativeLastSeen(
  isoDate: string | null,
  t: (k: string) => string,
): string {
  if (!isoDate) return t('hr.lastSeenNever');
  const then = new Date(`${isoDate}T12:00:00Z`).getTime();
  if (Number.isNaN(then)) return t('hr.lastSeenNever');
  const diffMs = Date.now() - then;
  const days = Math.max(0, Math.floor(diffMs / 86400000));
  if (days === 0) return t('hr.lastSeenToday');
  if (days === 1) return t('hr.lastSeenYesterday');
  if (days < 7) return t('hr.lastSeenDaysAgo').replace('{n}', String(days));
  if (days < 30) {
    const weeks = Math.floor(days / 7);
    return t('hr.lastSeenWeeksAgo').replace('{n}', String(weeks));
  }
  return t('hr.lastSeenOn').replace('{date}', isoDate);
}

function roleInterestLine(u: HrUserItem, t: (k: string) => string): string | null {
  const role = (u.job_title || []).filter(Boolean)[0] || '';
  const interestSkills = (u.skills || []).slice(0, 2).join(' / ');
  if (role && interestSkills) {
    return `${role} · ${t('hr.interestedIn').replace('{skills}', interestSkills)}`;
  }
  if (role) return role;
  if (interestSkills) return t('hr.interestedIn').replace('{skills}', interestSkills);
  return null;
}

function countActiveFilters(f: HrFilters): number {
  return f.jobTitles.length + (isDefaultTopPct(f) ? 0 : 1);
}

function buildActiveChips(
  f: HrFilters,
  t: (k: string) => string,
): Array<{ key: string; label: string }> {
  const chips = f.jobTitles.map((title) => ({
    key: `jobTitle:${title}`,
    label: title,
  }));
  if (!isDefaultTopPct(f)) {
    chips.push({
      key: 'topPct',
      label: t('hr.filterTopPercentRange')
        .replace('{from}', String(f.topPctFrom))
        .replace('{to}', String(f.topPctTo)),
    });
  }
  return chips;
}

function buildExportRows(users: HrUserItem[]) {
  return users.map((u) => ({
    id: u.id,
    rank: u.rank ?? '',
    username: u.username,
    firstName: u.firstName || '',
    lastName: u.lastName || '',
    email: u.email,
    phone: u.phone || '',
    university: u.university || '',
    job_title: (u.job_title || []).join('; '),
    skills: (u.skills || []).join('; '),
    solved: u.solved,
    acceptance: u.acceptance,
    streak: u.streak,
    points: u.points,
    integrity: u.integrity_trust_level,
  }));
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function exportUsers(users: HrUserItem[], format: ExportFormat) {
  const stamp = new Date().toISOString().slice(0, 10);
  const base = `sciplex-hr-export-${stamp}`;
  const rows = buildExportRows(users);

  if (format === 'json') {
    const payload = {
      exportedAt: new Date().toISOString(),
      count: rows.length,
      users: rows,
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], {
      type: 'application/json;charset=utf-8',
    });
    downloadBlob(blob, `${base}.json`);
    return;
  }

  if (format === 'csv') {
    const headers = Object.keys(rows[0] || {
      id: '',
      username: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      university: '',
      job_title: '',
      skills: '',
      solved: '',
      acceptance: '',
      streak: '',
      points: '',
      integrity: '',
    });
    const escape = (v: unknown) => {
      const s = v == null ? '' : String(v);
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const lines = [
      headers.join(','),
      ...rows.map((row) => headers.map((h) => escape((row as Record<string, unknown>)[h])).join(',')),
    ];
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    downloadBlob(blob, `${base}.csv`);
    return;
  }

  const sheet = XLSX.utils.json_to_sheet(rows);
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, 'Candidates');
  XLSX.writeFile(book, `${base}.xlsx`);
}

export default function HrPage() {
  const { t } = useTranslation();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState<HrUserItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);

  const [summary, setSummary] = useState({ activeCandidates: 0, highTrust: 0, highTrustShare: 0 });
  const [draft, setDraft] = useState<HrFilters>({ ...DEFAULT_FILTERS });
  const [applied, setApplied] = useState<HrFilters>({ ...DEFAULT_FILTERS });
  const [centerSearch, setCenterSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortBy>('solved');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [shortlist, setShortlist] = useState<number[]>([]);
  const [selectedBulk, setSelectedBulk] = useState<Set<number>>(() => new Set());
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  const closeExportMenu = useCallback(() => setExportMenuOpen(false), []);
  useClickOutside(exportMenuRef, closeExportMenu);

  const [jobTitleQuery, setJobTitleQuery] = useState('');
  const [jobTitleSuggestOpen, setJobTitleSuggestOpen] = useState(false);
  const [jobTitleHighlight, setJobTitleHighlight] = useState(0);
  const jobTitleSuggestRef = useRef<HTMLDivElement>(null);
  const closeJobTitleSuggest = useCallback(() => setJobTitleSuggestOpen(false), []);
  useClickOutside(jobTitleSuggestRef, closeJobTitleSuggest);

  const jobTitleSuggestions = useMemo(
    () => getJobTitleSuggestions(jobTitleQuery, [], 12),
    [jobTitleQuery],
  );

  const selectedJobTitleSet = useMemo(
    () => new Set(draft.jobTitles.map((t) => t.toLowerCase())),
    [draft.jobTitles],
  );

  const canAccess = useMemo(() => isHrOrAdmin(user?.role), [user?.role]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !user) {
      navigate('/login', { replace: true });
      return;
    }
    if (!isHrOrAdmin(user.role)) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  useEffect(() => {
    if (!user?.id) return;
    setShortlist(readShortlist(user.id));
    try {
      const raw = localStorage.getItem(savedSearchKey(user.id));
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<HrFilters> & { jobTitle?: string };
        let jobTitles = sanitizeFilterJobTitles(parsed?.jobTitles);
        if (
          jobTitles.length === 0
          && typeof parsed?.jobTitle === 'string'
          && isAllowedJobTitle(parsed.jobTitle)
        ) {
          const one = normalizeJobTitle(parsed.jobTitle);
          if (one) jobTitles = [one];
        }
        const next: HrFilters = {
          jobTitles,
          ...normalizeTopPctRange(parsed?.topPctFrom, parsed?.topPctTo),
        };
        setDraft(next);
        setApplied(next);
        setJobTitleQuery('');
      }
    } catch {
      /* ignore */
    }
  }, [user?.id]);

  useEffect(() => {
    if (!canAccess) return;
    const loadSummary = async () => {
      try {
        const r = await fetch(`${API_BASE}/api/hr/summary`, { credentials: 'include' });
        if (!r.ok) return;
        const data = await r.json();
        setSummary({
          activeCandidates: Number(data.activeCandidates) || 0,
          highTrust: Number(data.highTrust) || 0,
          highTrustShare: Number(data.highTrustShare) || 0,
        });
      } catch {
        /* ignore */
      }
    };
    loadSummary();
  }, [canAccess]);

  useEffect(() => {
    if (!canAccess) return;
    const controller = new AbortController();

    const fetchUsers = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('limit', '30');
        params.set('sortBy', sortBy);
        params.set('sortDir', sortDir);

        if (centerSearch.trim()) params.set('search', centerSearch.trim());
        for (const title of applied.jobTitles) {
          params.append('job_title', title);
        }
        if (!isDefaultTopPct(applied)) {
          params.set('top_pct_from', String(applied.topPctFrom));
          params.set('top_pct_to', String(applied.topPctTo));
        }

        const r = await fetch(`${API_BASE}/api/hr/users?${params}`, {
          credentials: 'include',
          signal: controller.signal,
        });
        if (r.status === 403 || r.status === 401) {
          navigate(r.status === 401 ? '/login' : '/', { replace: true });
          return;
        }
        if (!r.ok) throw new Error(t('hr.loadError'));
        const data = await r.json();
        const list: HrUserItem[] = Array.isArray(data?.users) ? data.users : [];
        setUsers(list);
        setTotal(typeof data?.total === 'number' ? data.total : 0);
        setTotalPages(typeof data?.totalPages === 'number' ? Math.max(1, data.totalPages) : 1);
        setSelectedId((prev) => {
          if (prev && list.some((u) => u.id === prev)) return prev;
          return list[0]?.id ?? null;
        });
      } catch (e) {
        if ((e as Error)?.name === 'AbortError') return;
        setError(e instanceof Error ? e.message : t('hr.loadError'));
      } finally {
        setLoading(false);
      }
    };

    const timer = window.setTimeout(fetchUsers, 200);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [canAccess, page, applied, sortBy, sortDir, centerSearch, navigate, t]);

  const selected = useMemo(
    () => users.find((u) => u.id === selectedId) || null,
    [users, selectedId],
  );

  const activeFilterCount = useMemo(() => countActiveFilters(applied), [applied]);
  const draftFilterCount = useMemo(() => countActiveFilters(draft), [draft]);
  const chips = useMemo(() => buildActiveChips(applied, t), [applied, t]);

  const applyFilters = () => {
    setApplied({ ...draft });
    setPage(1);
    setFiltersOpen(false);
  };

  const resetFilters = () => {
    setDraft({ ...DEFAULT_FILTERS });
    setApplied({ ...DEFAULT_FILTERS });
    setJobTitleQuery('');
    setJobTitleSuggestOpen(false);
    setPage(1);
  };

  const toggleJobTitle = (title: string) => {
    const normalized = normalizeJobTitle(title);
    if (!normalized) return;
    setDraft((prev) => {
      const exists = prev.jobTitles.some((t) => t.toLowerCase() === normalized.toLowerCase());
      if (exists) {
        return {
          ...prev,
          jobTitles: prev.jobTitles.filter((t) => t.toLowerCase() !== normalized.toLowerCase()),
        };
      }
      return { ...prev, jobTitles: [...prev.jobTitles, normalized] };
    });
    setJobTitleQuery('');
    setJobTitleSuggestOpen(true);
    setJobTitleHighlight(0);
  };

  const removeDraftJobTitle = (title: string) => {
    setDraft((prev) => ({
      ...prev,
      jobTitles: prev.jobTitles.filter((t) => t !== title),
    }));
  };

  const handleJobTitleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setJobTitleSuggestOpen(false);
      return;
    }
    if (e.key === 'Backspace' && !jobTitleQuery && draft.jobTitles.length > 0) {
      removeDraftJobTitle(draft.jobTitles[draft.jobTitles.length - 1]);
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (jobTitleSuggestions.length === 0) return;
      setJobTitleSuggestOpen(true);
      setJobTitleHighlight((i) => (i + 1) % jobTitleSuggestions.length);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (jobTitleSuggestions.length === 0) return;
      setJobTitleSuggestOpen(true);
      setJobTitleHighlight(
        (i) => (i - 1 + jobTitleSuggestions.length) % jobTitleSuggestions.length,
      );
      return;
    }
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (jobTitleSuggestOpen && jobTitleSuggestions[jobTitleHighlight]) {
      toggleJobTitle(jobTitleSuggestions[jobTitleHighlight]);
      return;
    }
    const exact = jobTitleSuggestions.find(
      (t) => t.toLowerCase() === jobTitleQuery.trim().toLowerCase(),
    );
    if (exact) toggleJobTitle(exact);
  };

  const removeChip = (key: string) => {
    if (key === 'topPct') {
      const next = { ...applied, topPctFrom: 0, topPctTo: 100 };
      setDraft(next);
      setApplied(next);
      setPage(1);
      return;
    }
    if (!key.startsWith('jobTitle:')) return;
    const title = key.slice('jobTitle:'.length);
    const next = {
      ...applied,
      jobTitles: applied.jobTitles.filter((t) => t !== title),
    };
    setDraft(next);
    setApplied(next);
    setPage(1);
  };

  const setDraftTopPctFrom = (value: number) => {
    setDraft((prev) => {
      const topPctFrom = clampPercent(value, prev.topPctFrom);
      return {
        ...prev,
        topPctFrom,
        topPctTo: Math.max(prev.topPctTo, topPctFrom),
      };
    });
  };

  const setDraftTopPctTo = (value: number) => {
    setDraft((prev) => {
      const topPctTo = clampPercent(value, prev.topPctTo);
      return {
        ...prev,
        topPctTo,
        topPctFrom: Math.min(prev.topPctFrom, topPctTo),
      };
    });
  };

  const saveSearch = () => {
    if (!user?.id) return;
    try {
      localStorage.setItem(savedSearchKey(user.id), JSON.stringify(applied));
    } catch {
      /* ignore */
    }
  };

  const toggleShortlist = useCallback(
    (id: number) => {
      setShortlist((prev) => {
        const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
        writeShortlist(user?.id, next);
        return next;
      });
    },
    [user?.id],
  );

  const toggleBulk = (id: number) => {
    setSelectedBulk((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (authLoading || !user) {
    return (
      <div className="hr-page">
        <div className="hr-loading">{t('hr.loading')}</div>
      </div>
    );
  }

  if (!canAccess) return null;

  return (
    <div className="hr-page">
      <div className="hr-shell">
        <header className="hr-header">
          <div className="hr-header__titles">
            <h1 className="hr-header__title">{t('hr.title')}</h1>
            <p className="hr-header__subtitle">{t('hr.subtitle')}</p>
          </div>
          <div className="hr-metrics">
            <div className="hr-metric-card">
              <div className="hr-metric-card__label">{t('hr.metricActive')}</div>
              <div className="hr-metric-card__value">{summary.activeCandidates.toLocaleString()}</div>
            </div>
            <div className="hr-metric-card">
              <div className="hr-metric-card__label">{t('hr.metricHighTrust')}</div>
              <div className="hr-metric-card__value">{summary.highTrust.toLocaleString()}</div>
              <div className="hr-metric-card__hint">
                {summary.highTrustShare}% {t('hr.metricOfTotal')}
              </div>
            </div>
            <div className="hr-metric-card">
              <div className="hr-metric-card__label">{t('hr.metricFit')}</div>
              <div className="hr-metric-card__value">{total.toLocaleString()}</div>
            </div>
            <div className="hr-metric-card hr-metric-card--accent">
              <div className="hr-metric-card__label">{t('hr.metricShortlist')}</div>
              <div className="hr-metric-card__value">{shortlist.length.toLocaleString()}</div>
            </div>
          </div>
        </header>

        <div className="hr-body">
          <aside className={`hr-filters ${filtersOpen ? 'is-open' : ''}`}>
            <div className="hr-filters__head">
              <h2 className="hr-filters__title">{t('hr.filters')}</h2>
              <button
                type="button"
                className="hr-filters__close"
                onClick={() => setFiltersOpen(false)}
                aria-label={t('hr.closeFilters')}
              >
                ×
              </button>
            </div>

            <div className="hr-field">
              <span className="hr-field__label">{t('hr.filterJobTitle')}</span>
              <div className="hr-combobox" ref={jobTitleSuggestRef}>
                <div className="hr-combobox__row">
                  <input
                    id="hr-job-title-filter"
                    type="text"
                    className="hr-input"
                    value={jobTitleQuery}
                    onChange={(e) => {
                      setJobTitleQuery(e.target.value);
                      setJobTitleSuggestOpen(true);
                      setJobTitleHighlight(0);
                    }}
                    onFocus={() => {
                      setJobTitleSuggestOpen(true);
                      setJobTitleHighlight(0);
                    }}
                    onKeyDown={handleJobTitleKeyDown}
                    placeholder={t('hr.filterJobTitleSearch')}
                    autoComplete="off"
                    role="combobox"
                    aria-expanded={jobTitleSuggestOpen && jobTitleSuggestions.length > 0}
                    aria-autocomplete="list"
                    aria-controls="hr-job-title-listbox"
                  />
                  {jobTitleQuery && (
                    <button
                      type="button"
                      className="hr-combobox__clear"
                      onClick={() => {
                        setJobTitleQuery('');
                        setJobTitleHighlight(0);
                      }}
                      aria-label={t('hr.resetFilters')}
                    >
                      ×
                    </button>
                  )}
                </div>
                {jobTitleSuggestOpen && jobTitleSuggestions.length > 0 && (
                  <ul
                    id="hr-job-title-listbox"
                    className="hr-combobox__menu"
                    role="listbox"
                    aria-multiselectable="true"
                  >
                    {jobTitleSuggestions.map((title, idx) => {
                      const isSelected = selectedJobTitleSet.has(title.toLowerCase());
                      return (
                        <li key={title} role="presentation">
                          <button
                            type="button"
                            className={`hr-combobox__item${idx === jobTitleHighlight ? ' is-active' : ''}${isSelected ? ' is-selected' : ''}`}
                            role="option"
                            aria-selected={isSelected}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => toggleJobTitle(title)}
                            onMouseEnter={() => setJobTitleHighlight(idx)}
                          >
                            <span
                              className={`hr-combobox__check${isSelected ? ' is-on' : ''}`}
                              aria-hidden="true"
                            >
                              {isSelected ? '✓' : ''}
                            </span>
                            <span className="hr-combobox__item-label">{title}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
              {draft.jobTitles.length > 0 ? (
                <div className="hr-combobox__chips">
                  {draft.jobTitles.map((title) => (
                    <span key={title} className="hr-combobox__chip">
                      {title}
                      <button
                        type="button"
                        onClick={() => removeDraftJobTitle(title)}
                        aria-label={t('hr.filterJobTitleRemove')}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <p className="hr-combobox__hint">{t('hr.filterJobTitleHint')}</p>
              )}
            </div>

            <div className="hr-field">
              <span className="hr-field__label">{t('hr.filterTopPercent')}</span>
              <div className="hr-dual-range">
                <div className="hr-dual-range__value">
                  {t('hr.filterTopPercentRange')
                    .replace('{from}', String(draft.topPctFrom))
                    .replace('{to}', String(draft.topPctTo))}
                </div>
                <div className="hr-dual-range__row">
                  <label className="hr-dual-range__num">
                    <input
                      type="number"
                      className="hr-input hr-input--compact hr-input--no-spin"
                      min={0}
                      max={100}
                      value={draft.topPctFrom}
                      onChange={(e) => setDraftTopPctFrom(Number(e.target.value))}
                      aria-label={t('hr.filterTopPercentFrom')}
                    />
                  </label>
                  <div
                    className="hr-dual-range__sliders"
                    style={{
                      ['--hr-range-from' as string]: String(draft.topPctFrom),
                      ['--hr-range-to' as string]: String(draft.topPctTo),
                    }}
                  >
                    <div className="hr-dual-range__track" aria-hidden="true">
                      <div className="hr-dual-range__fill" />
                    </div>
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={draft.topPctFrom}
                      onChange={(e) => setDraftTopPctFrom(Number(e.target.value))}
                      aria-label={t('hr.filterTopPercentFrom')}
                    />
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={draft.topPctTo}
                      onChange={(e) => setDraftTopPctTo(Number(e.target.value))}
                      aria-label={t('hr.filterTopPercentTo')}
                    />
                  </div>
                  <label className="hr-dual-range__num">
                    <input
                      type="number"
                      className="hr-input hr-input--compact hr-input--no-spin"
                      min={0}
                      max={100}
                      value={draft.topPctTo}
                      onChange={(e) => setDraftTopPctTo(Number(e.target.value))}
                      aria-label={t('hr.filterTopPercentTo')}
                    />
                  </label>
                </div>
                <p className="hr-combobox__hint">{t('hr.filterTopPercentHint')}</p>
              </div>
            </div>

            <div className="hr-filters__actions">
              <button type="button" className="hr-btn hr-btn--ghost" onClick={resetFilters}>
                {t('hr.resetFilters')}
              </button>
              <button type="button" className="hr-btn hr-btn--primary" onClick={applyFilters}>
                {t('hr.applyFilters')}
                {draftFilterCount > 0 && <span className="hr-btn__badge">{draftFilterCount}</span>}
              </button>
            </div>
          </aside>

          <section className="hr-main">
            <div className="hr-toolbar">
              <button
                type="button"
                className="hr-btn hr-btn--ghost hr-toolbar__filters-btn"
                onClick={() => setFiltersOpen(true)}
              >
                {t('hr.filters')}
                {activeFilterCount > 0 && <span className="hr-btn__badge">{activeFilterCount}</span>}
              </button>
              <input
                className="hr-input hr-toolbar__search"
                value={centerSearch}
                onChange={(e) => {
                  setCenterSearch(e.target.value);
                  setPage(1);
                }}
                placeholder={t('hr.centerSearchPlaceholder')}
              />
              <select
                className="hr-select"
                value={`${sortBy}:${sortDir}`}
                onChange={(e) => {
                  const [sb, sd] = e.target.value.split(':') as [SortBy, 'asc' | 'desc'];
                  setSortBy(sb);
                  setSortDir(sd);
                  setPage(1);
                }}
              >
                <option value="rank:asc">{t('hr.sortRankAsc')}</option>
                <option value="solved:desc">{t('hr.sortSolvedDesc')}</option>
                <option value="acceptance:desc">{t('hr.sortAcceptanceDesc')}</option>
                <option value="streak:desc">{t('hr.sortStreakDesc')}</option>
                <option value="points:desc">{t('hr.sortPointsDesc')}</option>
                <option value="createdAt:desc">{t('hr.sortNewest')}</option>
                <option value="username:asc">{t('hr.sortUsername')}</option>
              </select>
              <button type="button" className="hr-btn hr-btn--ghost" onClick={saveSearch}>
                {t('hr.saveSearch')}
              </button>
              <div className="hr-export" ref={exportMenuRef}>
                <button
                  type="button"
                  className="hr-btn hr-btn--ghost hr-export__toggle"
                  onClick={() => setExportMenuOpen((v) => !v)}
                  disabled={users.length === 0}
                  aria-expanded={exportMenuOpen}
                  aria-haspopup="menu"
                >
                  {t('hr.export')}
                  <span className="hr-export__chevron" aria-hidden>
                    ▾
                  </span>
                </button>
                {exportMenuOpen && (
                  <div className="hr-export__menu" role="menu">
                    {EXPORT_FORMATS.map((format) => (
                      <button
                        key={format}
                        type="button"
                        className="hr-export__item"
                        role="menuitem"
                        onClick={() => {
                          exportUsers(users, format);
                          setExportMenuOpen(false);
                        }}
                      >
                        <span className="hr-export__format">{format.toUpperCase()}</span>
                        <span className="hr-export__hint">{t(`hr.exportFormat.${format}`)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {chips.length > 0 && (
              <div className="hr-chips">
                {chips.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    className="hr-tag hr-tag--removable"
                    onClick={() => removeChip(c.key)}
                  >
                    {c.label} ×
                  </button>
                ))}
              </div>
            )}

            <div className="hr-list-meta">
              {t('hr.foundCount').replace('{count}', String(total))}
            </div>

            {error && (
              <div className="hr-error" role="alert">
                {error}
              </div>
            )}

            {loading ? (
              <div className="hr-loading hr-loading--inline">{t('hr.loadingUsers')}</div>
            ) : users.length === 0 ? (
              <div className="hr-empty">{t('hr.noUsers')}</div>
            ) : (
              <ul className="hr-candidate-list">
                {users.map((u) => {
                  const name = formatName(u);
                  const inShortlist = shortlist.includes(u.id);
                  const isSelected = selectedId === u.id;
                  const skills = u.skills || [];
                  const visibleSkills = skills.slice(0, 5);
                  const extraSkills = Math.max(0, skills.length - visibleSkills.length);
                  const roleLine = roleInterestLine(u, t);
                  const achievements = (u.achievements || []).slice(0, 3);
                  return (
                    <li
                      key={u.id}
                      className={`hr-candidate ${isSelected ? 'is-selected' : ''}`}
                      onClick={() => setSelectedId(u.id)}
                    >
                      <div className="hr-candidate__lead">
                        <label className="hr-candidate__check" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedBulk.has(u.id)}
                            onChange={() => toggleBulk(u.id)}
                            aria-label={name}
                          />
                        </label>

                        <div className="hr-candidate__profile">
                          <div className="hr-candidate__identity">
                            <div className="hr-candidate__name-row">
                              {u.rank != null && (
                                <span className="hr-rank" title={t('hr.rankLabel')}>
                                  #{u.rank}
                                </span>
                              )}
                              <span className="hr-candidate__name">{name}</span>
                              {isEmailVerified(u.email_verified) && (
                                <span className="hr-verified" title={t('hr.emailVerifiedYes')}>
                                  ✓
                                </span>
                              )}
                            </div>
                            <div className="hr-candidate__handle">@{u.username}</div>
                            {roleLine && <div className="hr-candidate__role">{roleLine}</div>}
                            {u.university && (
                              <div className="hr-candidate__meta-row">
                                <span className="hr-candidate__meta-icon" aria-hidden>
                                  🎓
                                </span>
                                <span>{u.university}</span>
                              </div>
                            )}
                            {skills.length > 0 && (
                              <div className="hr-candidate__skills">
                                {visibleSkills.map((s) => (
                                  <span key={s} className="hr-skill-pill">
                                    {s}
                                  </span>
                                ))}
                                {extraSkills > 0 && (
                                  <span className="hr-skill-pill hr-skill-pill--more">
                                    +{extraSkills}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="hr-candidate__stats">
                        <div className="hr-candidate__stat">
                          <span className="hr-candidate__stat-label">{t('hr.statSolved')}:</span>
                          <span className="hr-candidate__stat-value">
                            {formatStatNumber(u.solved)}
                          </span>
                        </div>
                        <div className="hr-candidate__stat">
                          <span className="hr-candidate__stat-label">{t('hr.statAcceptance')}:</span>
                          <span className="hr-candidate__stat-value">{u.acceptance}%</span>
                        </div>
                        <div className="hr-candidate__stat">
                          <span className="hr-candidate__stat-label">{t('hr.statStreak')}:</span>
                          <span className="hr-candidate__stat-value">
                            {t('hr.streakDays').replace('{n}', String(u.streak))}
                            {u.streak >= 7 ? ' 🔥' : ''}
                          </span>
                        </div>
                        <div className="hr-candidate__stat hr-candidate__stat--muted">
                          {formatRelativeLastSeen(u.lastActivityDate, t)}
                        </div>
                      </div>

                      <div className="hr-candidate__side">
                        {achievements.length > 0 && (
                          <div className="hr-candidate__badges">
                            {achievements.map((a) => (
                              <div key={a.id} className="hr-hex-badge" title={localizeAchievementName(t, a)}>
                                {a.icon ? (
                                  <img
                                    src={`/achievement-icons/${a.icon}`}
                                    alt=""
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).style.display = 'none';
                                    }}
                                  />
                                ) : (
                                  <span className="hr-hex-badge__fallback">★</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        <div
                          className={`hr-trust-inline hr-trust-inline--${u.integrity_trust_level || 'trusted'}`}
                        >
                          <span className="hr-trust-inline__icon" aria-hidden>
                            🛡
                          </span>
                          {trustLabel(u.integrity_trust_level, t)}
                        </div>
                      </div>

                      <div className="hr-candidate__actions" onClick={(e) => e.stopPropagation()}>
                        <Link
                          className="hr-candidate__action"
                          to={`/profile/${encodeURIComponent(u.username)}`}
                        >
                          <span className="hr-candidate__action-icon" aria-hidden>
                            ↗
                          </span>
                          {t('hr.openProfile')}
                        </Link>
                        <button
                          type="button"
                          className="hr-candidate__action"
                          onClick={() => toggleShortlist(u.id)}
                        >
                          <span className="hr-candidate__action-icon" aria-hidden>
                            ★
                          </span>
                          {inShortlist ? t('hr.inShortlist') : t('hr.toShortlist')}
                        </button>
                        <button
                          type="button"
                          className="hr-candidate__action"
                          onClick={() => setSelectedId(u.id)}
                        >
                          <span className="hr-candidate__action-icon" aria-hidden>
                            ⇄
                          </span>
                          {t('hr.compare')}
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}

            <div className="hr-pagination">
              <button
                type="button"
                className="hr-btn hr-btn--ghost"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                {t('hr.paginationBack')}
              </button>
              <span>
                {page} / {totalPages}
              </span>
              <button
                type="button"
                className="hr-btn hr-btn--ghost"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                {t('hr.paginationNext')}
              </button>
            </div>
          </section>

          <aside className="hr-preview">
            {selected ? (
              <>
                <div className="hr-preview__hero">
                  <div>
                    <h2 className="hr-preview__name">
                      {selected.rank != null && (
                        <span className="hr-rank" title={t('hr.rankLabel')}>
                          #{selected.rank}
                        </span>
                      )}
                      {formatName(selected)}
                      {isEmailVerified(selected.email_verified) && (
                        <span className="hr-verified">✓</span>
                      )}
                    </h2>
                    <div className="hr-preview__handle">@{selected.username}</div>
                    {(selected.job_title || [])[0] && (
                      <div className="hr-preview__role">{(selected.job_title || [])[0]}</div>
                    )}
                    {selected.university && (
                      <div className="hr-preview__meta">{selected.university}</div>
                    )}
                  </div>
                </div>

                <p className="hr-preview__bio">
                  {(selected.job_title || []).length > 0
                    ? t('hr.bioWithRole')
                        .replace('{role}', (selected.job_title || []).join(', '))
                        .replace(
                          '{skills}',
                          (selected.skills || []).slice(0, 4).join(', ') || t('hr.bioSkillsFallback'),
                        )
                    : t('hr.bioFallback')}
                </p>

                {(selected.skills || []).length > 0 && (
                  <div className="hr-field">
                    <span className="hr-field__label">{t('hr.keySkills')}</span>
                    <div className="hr-tags">
                      {(selected.skills || []).map((s) => (
                        <span key={s} className="hr-tag">
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <dl className="hr-preview__metrics">
                  <div>
                    <dt>{t('hr.statSolved')}</dt>
                    <dd>{selected.solved}</dd>
                  </div>
                  <div>
                    <dt>{t('hr.statAcceptance')}</dt>
                    <dd>{selected.acceptance}%</dd>
                  </div>
                  <div>
                    <dt>{t('hr.statPoints')}</dt>
                    <dd>{selected.points}</dd>
                  </div>
                  <div>
                    <dt>{t('hr.statStreak')}</dt>
                    <dd>{selected.streak}</dd>
                  </div>
                </dl>

                <div
                  className={`hr-trust-banner hr-trust-banner--${selected.integrity_trust_level || 'trusted'}`}
                >
                  {trustLabel(selected.integrity_trust_level, t)}
                </div>

                {(selected.achievements || []).length > 0 && (
                  <div className="hr-field">
                    <span className="hr-field__label">{t('hr.topAchievements')}</span>
                    <div className="hr-achievements">
                      {(selected.achievements || []).slice(0, 4).map((a) => (
                        <div key={a.id} className="hr-ach-card" title={localizeAchievementName(t, a)}>
                          {a.icon ? (
                            <img src={`/achievement-icons/${a.icon}`} alt="" />
                          ) : (
                            <span className="hr-ach-card__fallback">★</span>
                          )}
                          <span>{localizeAchievementName(t, a)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="hr-preview__cta">
                  <a
                    className="hr-btn hr-btn--primary hr-btn--block"
                    href={`mailto:${encodeURIComponent(selected.email)}`}
                  >
                    {t('hr.contact')}
                  </a>
                  <div className="hr-preview__cta-row">
                    <Link
                      className="hr-btn hr-btn--ghost"
                      to={`/profile/${encodeURIComponent(selected.username)}`}
                    >
                      {t('hr.invite')}
                    </Link>
                    <button
                      type="button"
                      className="hr-btn hr-btn--ghost"
                      onClick={() => toggleShortlist(selected.id)}
                    >
                      {shortlist.includes(selected.id) ? t('hr.inShortlist') : t('hr.toShortlist')}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="hr-empty">{t('hr.selectCandidate')}</div>
            )}
          </aside>
        </div>
      </div>

      {filtersOpen && (
        <button
          type="button"
          className="hr-filters-backdrop"
          aria-label={t('hr.closeFilters')}
          onClick={() => setFiltersOpen(false)}
        />
      )}
    </div>
  );
}
